import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { useAcceptedReviewQuery } from 'components/hooks/acceptedReviewQueries';

import ModalBox from 'components/application/ModalBox';
import ProfilePhotoDisplay from 'components/application/ProfilePhotoDisplay';

import { asFormattedDateTime } from 'components/helpers/dates';
import { personDisplayName } from 'components/helpers/users';

export default function AcceptedReviewModal({ onClose, reviewId }) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'projectPersonnel.components.AcceptedReviewModal',
  });

  const { t: i18nTProjects } = useTranslation('common', {
    keyPrefix: 'projects',
  });

  const {
    data: domainAcceptedReview,
    isSuccess: isDomainAcceptedReviewSuccess,
  } = useAcceptedReviewQuery({ reviewId });

  if (!isDomainAcceptedReviewSuccess) return null;

  const {
    data: {
      attributes: { signedAt },
      relationships: {
        projectPersonnel: {
          data: { id: projectPersonnelId },
        },
        reviewedVersion: {
          data: { id: reviewedVersionId },
        },
        signature: { data: signatureData },
      },
    },
    included,
  } = domainAcceptedReview;

  const {
    attributes: { firstName, lastName },
    relationships: {
      profilePhoto: { data: profilePhotoData },
    },
  } =
    included.personnel[
      included.projectPersonnel[projectPersonnelId].relationships.personnel.data
        .id
    ];

  const personnelDisplayName = personDisplayName({
    attributes: { firstName, lastName },
  });

  const profilePhoto =
    profilePhotoData && included.profilePhoto[profilePhotoData.id];

  const {
    attributes: { displayNumber: versionNumber },
  } = included.version[reviewedVersionId];

  const signature = signatureData && included.signature[signatureData.id];

  return (
    <ModalBox isOpen={true} onClose={onClose}>
      <div className='tw-flex tw-items-center tw-border-b-1 tw-border-t-1 tw-border-solid tw-border-grey-100 tw-px-6 tw-py-3'>
        <div className='tw-mr-3'>
          <ProfilePhotoDisplay
            componentSize='md'
            photoUrl={profilePhoto?.links?.croppedUrl}
            profileInitials={`${firstName[0]}${lastName[0]}`}
          />
        </div>
        <div>
          <h2 className='tw-m-0 tw-font-inter tw-text-l tw-font-semibold tw-tracking-auto tw-text-grey-900'>
            {i18nT('title', {
              displayName: personnelDisplayName,
              interpolation: { escapeValue: false },
            })}
          </h2>
          <p className='tw-m-0'>
            {i18nTProjects('versionNumberOnDateTime', {
              dateTime: asFormattedDateTime({ date: signedAt }),
              versionNumber,
            })}
          </p>
        </div>
      </div>
      <div className='popup__body'>
        <div className='popup__body-light'>
          {signature &&
            (() => {
              const {
                attributes: { attachedUrl: signatureUrl },
                relationships: {
                  photo: { data: photoData },
                },
              } = signature;

              const photo = photoData && included.photo[photoData.id];

              return (
                <>
                  {photo && (
                    <div className='popup-photo'>
                      <div className='popup-photo__wrapper tw-border-grey-100'>
                        <img
                          alt={personnelDisplayName}
                          src={photo.attributes.attachedUrl}
                        />
                      </div>
                    </div>
                  )}
                  <div
                    className={classNames(
                      'popup-signature',
                      photo && 'popup-signature--popup-photo',
                    )}
                  >
                    <div className='popup-signature__wrapper tw-border-grey-100'>
                      <img alt='Signature' src={signatureUrl} />
                    </div>
                  </div>
                </>
              );
            })()}
        </div>
      </div>
    </ModalBox>
  );
}

AcceptedReviewModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  reviewId: PropTypes.string.isRequired,
};
